import React, { useEffect, useState } from "react";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import api from "../lib/api";
import { useContentApi } from "../context/ContentContext";
import { formatApiError } from "../lib/errors";
import { useSharedPayPal } from "../context/PayPalContext";

/**
 * PayPalCheckoutButtons — shared Smart Buttons component used by Donate,
 * Shop checkout, and Raffle entry pages.
 *
 * Props
 *   purpose         "donation" | "shop" | "raffle"
 *   amount          number (USD) — required
 *   raffleId        string — required when purpose === "raffle"
 *   cartId          string — required when purpose === "shop"
 *   referenceId     string — optional; passed to PayPal as reference_id
 *   donorName       string — optional pre-fill
 *   donorEmail      string — optional pre-fill
 *   description     string — shown on the PayPal page
 *   onSuccess       (captureData) => void
 *   onCancel        () => void   — optional
 *   onError         (err) => void — optional
 *
 * Raffle-mode special behavior: locks the SDK funding sources to Venmo + Pay
 * with Bank only by disabling card / credit / paylater. This keeps the flow
 * Montana-compliant (no credit-card raffle ticket purchases).
 */
const PayPalCheckoutButtons = (props) => {
    const {
        purpose,
        amount,
        raffleId,
        raffleEntryId,
        cartId,
        campaignId,
        referenceId,
        donorName = "",
        donorEmail = "",
        description = "",
        onSuccess,
        onCancel,
        onError,
    } = props;
    const { config, loadError, requestLoad } = useSharedPayPal();
    // Defer PayPal SDK mount until the main thread has been idle for ~1.5s.
    // The PayPal SDK is ~500KB and does WebGL + font lookups synchronously
    // on mount — which can lock up the page while the user is typing into
    // Stripe's card field. Letting Stripe + the form render first, THEN
    // mounting PayPal, eliminates the typing-hang.
    const [sdkReady, setSdkReady] = useState(false);
    const { c } = useContentApi();

    useEffect(() => {
        let cancelled = false;
        const fire = () => { if (!cancelled) setSdkReady(true); };
        // Prefer requestIdleCallback when available; fall back to setTimeout.
        const ric = typeof window !== "undefined" && window.requestIdleCallback;
        if (ric) {
            const handle = ric(fire, { timeout: 2000 });
            return () => { cancelled = true; if (window.cancelIdleCallback) window.cancelIdleCallback(handle); };
        }
        const t = setTimeout(fire, 1500);
        return () => { cancelled = true; clearTimeout(t); };
    }, []);

    // Kill-switches: PayPal-only (`paypal.disabled`). The legacy master
    // `payments.disabled` flag is no longer wired into any UI, so we don't
    // check it here — the per-processor toggle is the single source of truth.
    const paymentsDisabled = c("paypal.disabled", "false") === "true";

    useEffect(() => {
        if (!paymentsDisabled && sdkReady) requestLoad();
    }, [paymentsDisabled, sdkReady, requestLoad]);

    const isRaffle = purpose === "raffle";

    // The root provider persists one SDK across routes. Only button instances unmount.

    // Kill-switch: when PayPal is disabled we render nothing at all.
    // The donor sees only the Stripe card form (or nothing if Stripe is also
    // disabled, but that's the admin's choice). No "temporarily offline"
    // banner — that was a recurring source of irritation.
    if (paymentsDisabled) {
        return null;
    }

    if (loadError) {
        return <p className="text-sm text-[#E53935] italic" data-testid="paypal-error">{loadError}</p>;
    }
    if (!config || !sdkReady) {
        return <p className="text-sm text-[#4A5568]" data-testid="paypal-loading">Loading PayPal…</p>;
    }
    if (!config.configured) {
        return <p className="text-sm text-[#E53935] italic">PayPal is not yet configured. Try again later.</p>;
    }

    return (
        <>
            <div data-testid={`paypal-buttons-${purpose}`}>
                <PayPalButtons
                    disabled={props.disabled}
                    // forceReRender intentionally only includes amount + currency.
                    // PayPalButtons internally watches this array; including the
                    // callbacks would defeat the memoisation and re-trigger the
                    // very `zoid destroyed` crash this whole rewrite avoids.
                    forceReRender={[amount, "USD", purpose]}
                    style={{ shape: "pill", layout: "vertical", label: purpose === "donation" ? "donate" : "pay", color: "gold" }}
                    createOrder={async () => {
                        try {
                            // Support two modes: (a) parent passes a static cartId/referenceId,
                            // (b) parent passes an async getReferenceId/getCartId that creates
                            // a pending order on-demand and returns its real id.
                            let effectiveCartId = cartId;
                            let effectiveRef = referenceId;
                            if (typeof props.getCartId === "function") {
                                effectiveCartId = await props.getCartId();
                            }
                            if (typeof props.getReferenceId === "function") {
                                effectiveRef = await props.getReferenceId();
                                effectiveCartId = effectiveCartId || effectiveRef;
                            }
                            const { data } = await api.post("/paypal/orders", {
                                purpose,
                                amount_usd: parseFloat(amount),
                                description: description || (
                                    purpose === "donation" ? "Reddog Foundation donation"
                                    : purpose === "shop" ? "Reddog Foundation shop order"
                                    : "Reddog Foundation raffle entry"
                                ),
                                reference_id: effectiveRef,
                                raffle_id: raffleId,
                                raffle_entry_id: raffleEntryId,
                                cart_id: effectiveCartId,
                                campaign_id: campaignId || undefined,
                                donor_email: donorEmail || undefined,
                                donor_name: donorName || undefined,
                            });
                            return data.order_id;
                        } catch (err) {
                            const msg = err?.response?.data?.detail || "Could not create PayPal order.";
                            toast.error(msg);
                            onError?.(err);
                            throw err;
                        }
                    }}
                    onApprove={async (data) => {
                        try {
                            const res = await api.post(`/paypal/orders/${data.orderID}/capture`);
                            toast.success("Payment complete — thank you!");
                            onSuccess?.(res.data);
                        } catch (err) {
                            toast.error(formatApiError(err, "Capture failed"));
                            onError?.(err);
                        }
                    }}
                    onCancel={() => { toast.info("Payment cancelled"); onCancel?.(); }}
                    onError={(err) => {
                        toast.error("PayPal error — please try again.");
                        onError?.(err);
                    }}
                />
            </div>
            {isRaffle && (
                <p className="mt-2 text-[11px] text-[#4A5568] italic" data-testid="paypal-raffle-notice">
                    Montana law: <strong>debit cards, Venmo, or PayPal balance / bank only</strong> for raffle entries. Credit cards will be automatically refunded by our system.
                </p>
            )}
        </>
    );
};

export default React.memo(PayPalCheckoutButtons);
